import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AnalysisEntryButton from '../../components/AnalysisEntryButton';
import CollectDashboardFrame from '../../components/collect/CollectDashboardFrame';
import { trackUiButtonEventAsync, UI_BUTTON_EVENTS } from '../../services/api/uiButtonStatsService';
import type { TFunction } from 'i18next';
import { useAppSelector } from '../../stores';
import { AppLayout } from '../../layouts';
import UserHeaderMenu from '../../layouts/UserHeaderMenu';
import { useHeaderLayoutCompanyData } from '../../hooks/useHeaderLayoutCompanyData';
import { useInviteOwnerModal } from '../../hooks/useInviteOwnerModal';
import {
    ApprovalRequestModal,
    InviteOwnerModal,
} from '../modal';
import {
    downloadCollectionExport,
    generateCollectDashboard,
    getCollectDashboard,
    getCollectDataCollection,
    getCollectionResponseMatrix,
    getSynthesis,
} from '../../services/api/collectDataService';
import { Routes } from '../../routes';
import { saveCollectInsightsPreload } from '../../utils/collectInsightsPreload';
import { resolveMapIdsForProcess, type SelectedCollectCollection, collectMatrixHasContributorResponses } from '../../utils/analysisContextLinks';
import { CompanyAnalysisService } from '../../services/api/companyAnalysisService';
import type { iMap } from '../../components/type';
import '../../features/process/process/process-create.css';

const tk = 'form.collectResults';

function planLabelFromStatus(t: TFunction, status?: string): string {
    if (!status) return t('layout.header.planUnknown');
    const s = String(status).toUpperCase();
    const keys: Record<string, string> = {
        FREE: 'layout.header.planFREE',
        ESSENTIAL: 'layout.header.planESSENTIAL',
        PLUS: 'layout.header.planPLUS',
        ENTERPRISE: 'layout.header.planENTERPRISE',
    };
    const key = keys[s];
    if (key) return t(key);
    return s.charAt(0) + s.slice(1).toLowerCase();
}

const SECTION_DOT_COLORS = ['#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6', '#14b8a6', '#f97316'];

const TOOL_NAMES = [
    'SAP',
    'Excel',
    'Outlook',
    'SharePoint',
    'Teams',
    'Power BI',
    'Workday',
    'Google Sheets',
    'Slack',
    'Word',
    'PowerPoint',
    'OneDrive',
    'Tableau',
    'Snowflake',
    'BigQuery',
    'Oracle',
];

type SynthTab = 'summary' | 'table';

type MatrixColumn = {
    question_id: string;
    question_text: string;
    section_id: string;
    section_name: string;
    section_order: number;
    question_order: number;
    response_count: number;
};

type MatrixRow = {
    contributor_id: string;
    contributor_name: string;
    contributor_role: string;
    status: string;
    answers: Record<string, string>;
};

type MatrixStats = {
    contributors_total: number;
    contributors_completed: number;
    kept_questions: number;
    questions_with_any_response: number;
    avg_lead_time_days: number | null;
};

type MatrixPayload = {
    columns: MatrixColumn[];
    rows: MatrixRow[];
    stats: MatrixStats;
};

function sectionDotColor(sectionIndex: number): string {
    return SECTION_DOT_COLORS[sectionIndex % SECTION_DOT_COLORS.length];
}

function shortenLabel(text: string, max = 44): string {
    const t = (text || '').trim();
    if (t.length <= max) return t;
    return `${t.slice(0, max - 1)}…`;
}

function respondentInitials(name: string): string {
    return (name || 'Y')
        .split(/\s+/)
        .filter(Boolean)
        .map((p) => p[0])
        .join('')
        .toUpperCase()
        .slice(0, 2) || 'Y';
}

function formatAvgLeadDays(d: number | null | undefined): string {
    if (d == null || Number.isNaN(d)) return '';
    if (d < 1 / 24) return `${Math.round(d * 24 * 60)}m`;
    if (d < 1) return `${(d * 24).toFixed(1)}h`;
    return `${d.toFixed(1)}d`;
}

function isLeadTimeColumn(sectionName: string, questionText: string): boolean {
    const blob = `${sectionName} ${questionText}`.toLowerCase();
    return /lead|time|duration|how long|delay|délai|fréquence|frequency|durée/i.test(blob);
}

function isToolsColumn(sectionName: string, questionText: string): boolean {
    const blob = `${sectionName} ${questionText}`.toLowerCase();
    return /tool|system|software|logiciel|application/i.test(blob);
}

function leadTimeCellTone(text: string): '' | 'warn' | 'danger' {
    if (!text) return '';
    const t = text.toLowerCase();
    if (/\(slow\)|\bslow\b|lent/i.test(t)) return 'danger';
    if (/\bup to\b|jusqu['']?à/i.test(t)) return 'warn';
    return '';
}

function extractToolsFromAnswer(text: string): string[] {
    const found: string[] = [];
    for (const k of TOOL_NAMES) {
        const re = new RegExp(`\\b${k.replace(/ /g, '\\s*')}\\b`, 'i');
        if (re.test(text)) found.push(k);
    }
    return found;
}

function stripKnownTools(text: string, tools: string[]): string {
    let rest = text;
    for (const tool of tools) {
        rest = rest.replace(new RegExp(`\\s*\\b${tool.replace(/ /g, '\\s*')}\\b\\s*`, 'gi'), ' ');
    }
    return rest.replace(/\s+/g, ' ').trim();
}

function TableCell({
    text,
    sectionName,
    questionText,
}: {
    text: string;
    sectionName: string;
    questionText: string;
}) {
    const raw = (text || '').trim();
    const toolsCol = isToolsColumn(sectionName, questionText);
    const tools = toolsCol ? extractToolsFromAnswer(raw) : [];
    const lead = isLeadTimeColumn(sectionName, questionText);
    const tone = lead ? leadTimeCellTone(raw) : '';

    if (toolsCol && tools.length > 0) {
        const extra = stripKnownTools(raw, tools);
        return (
            <div className="collect-synth-cell-inner">
                <div className="collect-synth-pill-row">
                    {tools.map((t) => (
                        <span key={t} className="collect-synth-pill">
                            {t}
                        </span>
                    ))}
                </div>
                {extra.length > 2 ? <div className="collect-synth-cell-extra">{extra}</div> : null}
            </div>
        );
    }

    return (
        <div
            className={`collect-synth-cell-text ${tone ? `collect-synth-cell--${tone}` : ''}`.trim()}
            style={{ whiteSpace: 'pre-wrap' }}
        >
            {raw || '—'}
        </div>
    );
}

const CollectDataSynthesisView = () => {
    const { collectionId } = useParams<{ collectionId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const auth = useAppSelector((s) => s.auth);
    const role = useAppSelector((s) => s.role.role);
    const company = useAppSelector((s) => s.company.company);
    const access_token = auth.access_token;
    const {
        loadcompany,
        listApproval,
        loading: approvalLoading,
        creditsAvailable,
        creditsLoading,
        companyById,
    } = useHeaderLayoutCompanyData({ includeMyCompanies: false });
    const inviteOwner = useInviteOwnerModal(t);

    const planLabel = useMemo(
        () => planLabelFromStatus(t, companyById?.status ?? company?.status),
        [t, companyById?.status, company?.status],
    );

    const [name, setName] = useState('');
    const [contributors, setContributors] = useState<any[]>([]);
    const [guide, setGuide] = useState<any>(null);
    const [items, setItems] = useState<any[]>([]);
    const [matrix, setMatrix] = useState<MatrixPayload | null>(null);
    const [matrixErr, setMatrixErr] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [tab, setTab] = useState<SynthTab>('summary');
    const [linkedProcessId, setLinkedProcessId] = useState<string | null>(null);
    const [mapIds, setMapIds] = useState<string[]>([]);
    const [objectiveText, setObjectiveText] = useState<string | null>(null);
    const [dashboardHtml, setDashboardHtml] = useState<string | null>(null);
    const [dashboardTitle, setDashboardTitle] = useState('');
    const [dashboardReady, setDashboardReady] = useState(false);
    const [generatingDashboard, setGeneratingDashboard] = useState(false);
    const [dashboardError, setDashboardError] = useState<string | null>(null);
    const autoGenerateStartedRef = useRef(false);
    const [appTheme, setAppTheme] = useState<'light' | 'dark'>('light');

    useEffect(() => {
        const readTheme = () => {
            const t = document.documentElement.getAttribute('data-theme');
            setAppTheme(t === 'dark' ? 'dark' : 'light');
        };
        readTheme();
        const obs = new MutationObserver(readTheme);
        obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
        return () => obs.disconnect();
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!collectionId || !access_token) return;
            setLoading(true);
            setErr(null);
            setMatrixErr(null);
            const [c, s, m] = await Promise.all([
                getCollectDataCollection(collectionId, access_token),
                getSynthesis(collectionId, access_token),
                getCollectionResponseMatrix(collectionId, access_token),
            ]);
            if (cancelled) return;
            if (c.status === 200 && c.body?.data) {
                if (c.body.data.collection) {
                    const coll = c.body.data.collection;
                    setName(coll.name || '');
                    setLinkedProcessId(coll.linked_process_id ?? null);
                    setMapIds(Array.isArray(coll.map_ids) ? coll.map_ids : []);
                    setObjectiveText(coll.objective_text ?? null);
                } else {
                    setLinkedProcessId(null);
                    setMapIds([]);
                    setObjectiveText(null);
                }
                setGuide(c.body.data.guide ?? null);
                setContributors(Array.isArray(c.body.data.contributors) ? c.body.data.contributors : []);
            }
            if (s.status === 200 && s.body?.data?.items) {
                setItems(s.body.data.items);
            }
            if (m.status === 200 && m.body?.data) {
                setMatrix({
                    columns: Array.isArray(m.body.data.columns) ? m.body.data.columns : [],
                    rows: Array.isArray(m.body.data.rows) ? m.body.data.rows : [],
                    stats: m.body.data.stats || {
                        contributors_total: 0,
                        contributors_completed: 0,
                        kept_questions: 0,
                        questions_with_any_response: 0,
                        avg_lead_time_days: null,
                    },
                });
            } else {
                setMatrix(null);
                setMatrixErr('load_failed');
            }
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [collectionId, access_token]);

    const hasCollectResponses = collectMatrixHasContributorResponses(matrix);

    const canGenerateDashboard =
        !loading && !err && hasCollectResponses && !generatingDashboard;

    const handleGenerateDashboard = useCallback(
        async (options?: { trackClick?: boolean }) => {
            if (!collectionId || !access_token || generatingDashboard) return;
            if (options?.trackClick) {
                await trackUiButtonEventAsync(UI_BUTTON_EVENTS.collect.synthesisGenerateDashboard);
            }
            setGeneratingDashboard(true);
            setDashboardError(null);
            const theme =
                typeof document !== 'undefined' &&
                document.documentElement.getAttribute('data-theme') === 'dark'
                    ? 'dark'
                    : 'light';
            const r = await generateCollectDashboard(collectionId, access_token, { theme });
            if (r.status !== 200 || !r.body?.data?.dashboard?.html) {
                const data = r.body?.data as { user_message?: string } | undefined;
                if (data?.user_message) {
                    setDashboardError(data.user_message);
                }
                setGeneratingDashboard(false);
                return;
            }
            const dash = r.body.data.dashboard;
            setDashboardHtml(dash.html);
            setDashboardTitle(dash.title || '');
            setGeneratingDashboard(false);
        },
        [collectionId, access_token, generatingDashboard],
    );

    const loadDashboard = useCallback(async () => {
        if (!collectionId || !access_token) return;
        setDashboardReady(false);
        setDashboardError(null);
        const r = await getCollectDashboard(collectionId, access_token, appTheme);
        if (r.status === 200 && r.body?.data) {
            const d = r.body.data as { dashboard?: { html?: string; title?: string } | null };
            if (d.dashboard?.html) {
                setDashboardHtml(d.dashboard.html);
                setDashboardTitle(d.dashboard.title || '');
            } else {
                setDashboardHtml(null);
                setDashboardTitle('');
            }
        }
        setDashboardReady(true);
    }, [collectionId, access_token, appTheme]);

    useEffect(() => {
        if (!loading && !err) {
            autoGenerateStartedRef.current = false;
            void loadDashboard();
        }
    }, [loading, err, loadDashboard]);

    useEffect(() => {
        if (!dashboardReady || loading || err || dashboardHtml || !canGenerateDashboard) return;
        if (autoGenerateStartedRef.current) return;
        autoGenerateStartedRef.current = true;
        void handleGenerateDashboard();
    }, [dashboardReady, loading, err, dashboardHtml, canGenerateDashboard, handleGenerateDashboard]);

    const keptFromGuide = useMemo(() => {
        type GuideSubSection = { questions?: Array<{ status?: string }> };
        type GuideRoleSection = { sections?: GuideSubSection[] };

        const countKept = (sections?: GuideSubSection[]) => {
            let n = 0;
            for (const sec of sections || []) {
                for (const q of sec.questions || []) {
                    if (q.status === 'kept') n += 1;
                }
            }
            return n;
        };
        if (guide?.role_sections?.length) {
            return (guide.role_sections as GuideRoleSection[]).reduce(
                (sum: number, rs: GuideRoleSection) => sum + countKept(rs.sections),
                0,
            );
        }
        return countKept(guide?.sections as GuideSubSection[] | undefined);
    }, [guide]);

    const stats = matrix?.stats;
    const totalContrib = stats?.contributors_total ?? contributors.length;
    const completedContrib =
        stats?.contributors_completed ??
        contributors.filter((c: any) => String(c.status || '').toLowerCase() === 'completed').length;
    const keptQ = stats?.kept_questions ?? keptFromGuide;
    const answeredQ = stats?.questions_with_any_response ?? 0;
    const avgDays = stats?.avg_lead_time_days ?? null;

    const responseBadgeDone = totalContrib > 0 ? Math.min(completedContrib, totalContrib) : 0;
    const allResponsesDone = totalContrib > 0 && completedContrib >= totalContrib;
    const completionPct =
        totalContrib > 0 ? Math.round((Math.min(completedContrib, totalContrib) / totalContrib) * 100) : 0;

    const avgLeadDisplay = formatAvgLeadDays(avgDays ?? undefined) || t(`${tk}.statAvgLeadEmpty`);

    const columnByQuestionId = useMemo(() => {
        const m: Record<string, MatrixColumn> = {};
        if (!matrix?.columns) return m;
        for (const col of matrix.columns) {
            m[col.question_id] = col;
        }
        return m;
    }, [matrix]);

    const sectionIndexBySectionId = useMemo(() => {
        const order: string[] = [];
        if (!matrix?.columns) return {} as Record<string, number>;
        for (const c of matrix.columns) {
            if (!order.includes(c.section_id)) order.push(c.section_id);
        }
        const map: Record<string, number> = {};
        order.forEach((id, i) => {
            map[id] = i;
        });
        return map;
    }, [matrix]);

    const headerGroups = useMemo(() => {
        if (!matrix?.columns?.length) return [] as { section_id: string; section_name: string; span: number }[];
        const groups: { section_id: string; section_name: string; span: number }[] = [];
        for (const col of matrix.columns) {
            const last = groups[groups.length - 1];
            if (last && last.section_id === col.section_id) last.span += 1;
            else groups.push({ section_id: col.section_id, section_name: col.section_name, span: 1 });
        }
        return groups;
    }, [matrix]);

    const onExport = async () => {
        if (!collectionId || !access_token) return;
        const r = await downloadCollectionExport(collectionId, access_token);
        if (!r.ok || !r.blob) return;
        const url = URL.createObjectURL(r.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `collect-${collectionId}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const onOpenAnalysis = async () => {
        if (!collectionId || !access_token) return;
        if (!collectMatrixHasContributorResponses(matrix)) return;
        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.collect.resultsOpenAnalysis);

        let resolvedMapIds = mapIds.filter(Boolean);
        const collectionEntry: SelectedCollectCollection = {
            id: collectionId,
            name: (name || '').trim() || collectionId,
            map_ids: resolvedMapIds,
            linked_process_id: linkedProcessId,
        };

        if (!resolvedMapIds.length && auth.session?.company_id) {
            try {
                const allMaps = (await CompanyAnalysisService.listMappingSummaries(
                    auth.session.company_id,
                    access_token,
                )) as iMap[];
                if (!resolvedMapIds.length && linkedProcessId) {
                    resolvedMapIds = resolveMapIdsForProcess(allMaps, linkedProcessId);
                }
            } catch {
                /* maps optional if linked later in Insights */
            }
        }

        const preload = {
            collectionId,
            collectionName: collectionEntry.name,
            mapIds: resolvedMapIds,
            linkedProcessId,
            objectiveText,
            synthesisOnly: true,
        };
        saveCollectInsightsPreload(preload);
        navigate(Routes.COMPANYANALYSIS, { state: { collectInsightsPreload: preload } });
    };

    const canOpenAnalysis = !loading && !err && collectMatrixHasContributorResponses(matrix);

    const topTitle = (name || '').trim() || t('words.loading');

    return (
        <AppLayout>
            <div className="collect-synth-page-root flex-grow-1 d-flex flex-column min-vh-0">
                <header className="collect-synth-topbar">
                    <div className="collect-synth-context-bar">
                        <div className="collect-synth-context-bar__left">
                            <div className="collect-synth-context-bar__title-row">
                                <Link
                                    to={`/process/data-collect/${collectionId}`}
                                    className="collect-synth-back-ctx"
                                    title={t(`${tk}.backToWorkspace`)}
                                >
                                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden>
                                        <path
                                            d="M10 3L5 8L10 13"
                                            stroke="currentColor"
                                            strokeWidth="1.4"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </Link>
                                <span
                                    className={`collect-synth-status-dot ${allResponsesDone && totalContrib > 0 ? 'on' : ''}`}
                                    aria-hidden
                                />
                                <span className="collect-synth-context-bar__title" title={topTitle}>
                                    {topTitle}
                                </span>
                                {totalContrib > 0 ? (
                                    <span className="collect-synth-context-bar__title-addon">
                                        <span className="collect-badge collect-synth-response-badge">
                                            {t(`${tk}.responseBadge`, {
                                                done: responseBadgeDone,
                                                total: totalContrib,
                                            })}
                                        </span>
                                    </span>
                                ) : null}
                            </div>
                        </div>
                        <div className="collect-synth-topbar__actions">
                            <button type="button" className="action-btn" onClick={onExport}>
                                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden>
                                    <path
                                        d="M8 2v8M5 7l3 3 3-3M3 12h10"
                                        stroke="currentColor"
                                        strokeWidth="1.3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                {t(`${tk}.exportXlsx`)}
                            </button>
                            <AnalysisEntryButton
                                onClick={() => void onOpenAnalysis()}
                                disabled={!canOpenAnalysis}
                            />
                            <UserHeaderMenu
                                t={t}
                                loadcompany={loadcompany}
                                showChangeCompanyInMenu={false}
                                fullname={auth.user?.fullname || auth.user?.email || ''}
                                email={auth.user?.email || ''}
                                role={role}
                                planLabel={planLabel}
                                creditsAvailable={creditsAvailable}
                                creditsLoading={creditsLoading}
                                isAdmin={role === 'ADMIN'}
                            />
                        </div>
                    </div>
                </header>

                <div className="pc-wrap collect-synth-root flex-grow-1 min-vh-0">
                    <div className="c-tabs collect-synth-tabs" role="tablist">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={tab === 'summary'}
                            className={`ctab ${tab === 'summary' ? 'active' : ''}`}
                            onClick={() => setTab('summary')}
                        >
                            {t(`${tk}.tabSummary`)}
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={tab === 'table'}
                            className={`ctab ${tab === 'table' ? 'active' : ''}`}
                            onClick={() => setTab('table')}
                        >
                            {t(`${tk}.tabTable`)}
                        </button>
                    </div>

                        {loading && <p className="collect-muted">{t('words.loading')}</p>}
                        {err && <div className="collect-alert danger">{err}</div>}

                        {!loading && tab === 'summary' && (
                            <div className="collect-synth-summary" role="tabpanel">
                                <section className="collect-dashboard-section mb-4">
                                    {(dashboardHtml || generatingDashboard || !dashboardReady) && (
                                        <div className="d-flex flex-wrap justify-content-end gap-2 mb-2">
                                            {dashboardHtml && (
                                                <button
                                                    type="button"
                                                    className="action-btn d-inline-flex align-items-center gap-2"
                                                    disabled={generatingDashboard}
                                                    onClick={() => void handleGenerateDashboard({ trackClick: true })}
                                                >
                                                    {generatingDashboard && (
                                                        <span
                                                            className="spinner-border spinner-border-sm"
                                                            role="status"
                                                            aria-hidden="true"
                                                            style={{ color: 'var(--tenant-primary, #017d7c)' }}
                                                        />
                                                    )}
                                                    {t(`${tk}.dashboardRegenerate`)}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {dashboardError && (
                                        <div className="collect-alert danger mb-2">{dashboardError}</div>
                                    )}
                                    <div className="collect-dashboard-viewport position-relative">
                                        {dashboardHtml && (
                                            <CollectDashboardFrame
                                                html={dashboardHtml}
                                                title={dashboardTitle}
                                                theme={appTheme}
                                                className={generatingDashboard ? 'collect-dashboard-frame--busy' : ''}
                                            />
                                        )}
                                        {!dashboardHtml && (generatingDashboard || !dashboardReady) && (
                                            <div
                                                className="collect-dashboard-placeholder"
                                                style={{ height: 420 }}
                                                aria-busy="true"
                                            />
                                        )}
                                        {generatingDashboard && (
                                            <div
                                                className="collect-dashboard-overlay d-flex align-items-center justify-content-center"
                                                aria-live="polite"
                                                aria-busy="true"
                                            >
                                                <span
                                                    className="spinner-border"
                                                    role="status"
                                                    style={{ width: '2.5rem', height: '2.5rem', color: 'var(--tenant-primary, #017d7c)' }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </section>
                            </div>
                        )}

                        {!loading && tab === 'table' && (
                            <div className="collect-synth-table-panel" role="tabpanel">
                                {matrixErr && <div className="collect-alert danger">{t(`${tk}.tableLoadError`)}</div>}
                                {!matrix?.columns?.length && !matrixErr && (
                                    <p className="collect-muted">{t(`${tk}.tableEmpty`)}</p>
                                )}
                                {matrix && matrix.columns.length > 0 && (
                                    <div className="collect-synth-table-wrap">
                                        <table className="collect-synth-table">
                                            <thead>
                                                <tr>
                                                    <th className="collect-synth-th-corner" rowSpan={2}>
                                                        {t(`${tk}.respondentCol`)}
                                                    </th>
                                                    {headerGroups.map((g) => (
                                                        <th
                                                            key={g.section_id}
                                                            className="collect-synth-th-group"
                                                            colSpan={g.span}
                                                        >
                                                            <span
                                                                className="synth-q-dot synth-q-dot--sm"
                                                                style={{
                                                                    background: sectionDotColor(
                                                                        sectionIndexBySectionId[g.section_id] ?? 0
                                                                    ),
                                                                }}
                                                            />
                                                            {g.section_name}
                                                        </th>
                                                    ))}
                                                </tr>
                                                <tr>
                                                    {matrix.columns.map((col) => (
                                                        <th key={col.question_id} className="collect-synth-th-q">
                                                            {shortenLabel(col.question_text, 36)}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {matrix.rows.map((row) => (
                                                    <tr key={row.contributor_id}>
                                                        <td className="collect-synth-td-name">
                                                            <div className="collect-synth-respondent">
                                                                <span className="collect-synth-r-av">
                                                                    {respondentInitials(row.contributor_name)}
                                                                </span>
                                                                <div>
                                                                    <div className="collect-synth-r-name">{row.contributor_name}</div>
                                                                    {row.contributor_role ? (
                                                                        <div className="collect-synth-r-role">{row.contributor_role}</div>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        {matrix.columns.map((col) => (
                                                            <td key={col.question_id} className="collect-synth-td-cell">
                                                                <TableCell
                                                                    text={row.answers[col.question_id] || ''}
                                                                    sectionName={col.section_name}
                                                                    questionText={col.question_text}
                                                                />
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                </div>
            </div>
            <InviteOwnerModal
                alert={inviteOwner.alert}
                email={inviteOwner.email}
                isSubmitting={inviteOwner.isSubmitting}
                handleChange={inviteOwner.handleChange}
                handleSubmit={inviteOwner.handleSubmit}
                t={t}
            />
            <ApprovalRequestModal approvalList={listApproval} loading={approvalLoading} />
        </AppLayout>
    );
};

export default CollectDataSynthesisView;
